import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";
import { StaffDialog, PERMISSIONS } from "./StaffDialog";

const permLabel = Object.fromEntries(PERMISSIONS);

export const StaffManager = ({ sites }) => {
  const [staff, setStaff] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const siteName = Object.fromEntries(sites.map((s) => [s.id, s.name]));
  const [branchName, setBranchName] = useState({});
  useEffect(() => { api.get("/branches").then((r) => setBranchName(Object.fromEntries(r.data.map((b) => [b.id, b.name])))); }, [sites]);

  const load = useCallback(() => api.get("/staff").then((r) => setStaff(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async (body) => {
    if (editing) await api.put(`/staff/${editing.id}`, body);
    else await api.post("/staff", body);
    toast.success(editing ? "Staff updated" : "Staff created");
    setOpen(false); setEditing(null); load();
  };

  const remove = (s) => window.confirm(`Remove staff "${s.name}"? Their entries stay.`) &&
    api.delete(`/staff/${s.id}`).then(() => { toast.success("Staff removed"); load(); }).catch((e) => toast.error(errMsg(e)));

  return (
    <div className="space-y-6" data-testid="staff-manager">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 rise">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-c">Admin</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Staff access</h1>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-none bg-brand hover:bg-brand/90 text-onbrand" data-testid="add-staff-button">
          <Plus className="h-4 w-4 mr-1" /> Add Staff
        </Button>
      </header>
      {staff.length === 0 ? (
        <div className="bg-surface border border-dashed border-line2 p-10 text-center" data-testid="staff-empty">
          <Users className="h-8 w-8 mx-auto text-muted-c" />
          <p className="mt-2 text-sm text-muted-c">No staff yet. Add one and choose which books and features they get.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {staff.map((s) => (
            <div key={s.id} className="bg-surface border border-line p-5 space-y-3 rise" data-testid={`staff-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{s.name}</p>
                  <p className="text-sm text-muted-c truncate">{s.email}</p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => { setEditing(s); setOpen(true); }} className="p-1.5 text-muted-c hover:text-brand" data-testid={`edit-staff-${s.id}`} aria-label="Edit staff"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(s)} className="p-1.5 text-muted-c hover:text-neg" data-testid={`delete-staff-${s.id}`} aria-label="Delete staff"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-c mb-1">Books</p>
                <div className="flex flex-wrap gap-1">
                  {s.site_ids.length ? s.site_ids.map((id) => <span key={id} className="text-xs border border-brand/60 text-brand px-2 py-0.5">{siteName[id] || "—"}</span>)
                    : <span className="text-xs text-muted-c">None</span>}
                </div>
                {s.branch_ids?.length > 0 && (
                  <p className="text-xs text-muted-c mt-1" data-testid={`staff-card-branches-${s.id}`}>Limited to branches: {s.branch_ids.map((b) => branchName[b] || "—").join(", ")}</p>
                )}
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-c mb-1">Permissions</p>
                <div className="flex flex-wrap gap-1">
                  {s.permissions.length ? s.permissions.map((p) => <span key={p} className="text-xs border border-line2 px-2 py-0.5">{permLabel[p]}</span>)
                    : <span className="text-xs text-muted-c">View only</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <StaffDialog open={open} staff={editing} sites={sites} onClose={() => { setOpen(false); setEditing(null); }} onSubmit={save} />
    </div>
  );
};
